import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import { useCallback, useState } from "react";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { Link } from "react-router-dom";
import { ActionIcon, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../../components/buttons/DeletePopover";
import { useFiltersPR } from "../../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../../components/table/components/TableFilter";
import CustomTable from "../../../../components/table/CustomTable";
import useFetch from "../../../../hooks/useFetch";
import useHandleErrors from "../../../../hooks/useHandleErrors";
import Page from '../../../../layout/Page/Page';
import { menuRoutes } from "../../../../router/menu";
import { KoReasonService } from "../../../../services/ko-reason/koReasonService";
import { KoReasonsApiResponse } from "../../../../type/entities/ko-reason-type";
import KoReasonsFilters from "./KoReasonsFilters";
import { entities as statusEntities } from "../../StateForm";

const getStatusEntityLabel = (statusEntity?: string | null) => {
    if (!statusEntity) return 'N/A';
    return statusEntities.find((entity) => entity.value === statusEntity)?.label ?? statusEntity;
};

const columnOrder = [
    'id',
    'name',
    'statusEntity',
    'description',
    'updatedAt',
    'createdAt'
];

const KoReasonsList = () => {
    const service = new KoReasonService();
    const { handleErrors } = useHandleErrors();

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listKoReasons(filters);
        return response.getResponseData() as KoReasonsApiResponse;
    }, [filters]));

    const listResult = data?.data ?? [];
    const orderedData = () => listResult.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteKoReason(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Motivo de KO eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiKoReasons(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Motivos de KO eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'ko-reasons-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['id']}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                return row.row.original.name ?? 'N/A';
                            }
                        },
                        {
                            key: 'statusEntity',
                            render: (row: any) => {
                                return getStatusEntityLabel(row.row.original.statusEntity);
                            }
                        },
                        {
                            key: 'description',
                            render: (row: any) => {
                                return row.row.original.description ?? 'N/A';
                            }
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const dateVal = row.row.original?.updatedAt;
                                const dateStr = typeof dateVal === 'object' && dateVal?.date ? dateVal.date : dateVal;
                                if (!dateStr) return 'N/A';
                                const parsed = new Date(dateStr);
                                return !isNaN(parsed.getTime()) ? capitalize(formatDistance(parsed, new Date(), { addSuffix: true, locale: es })) : 'N/A';
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const dateVal = row.row.original?.createdAt;
                                const dateStr = typeof dateVal === 'object' && dateVal?.date ? dateVal.date : dateVal;
                                if (!dateStr) return 'N/A';
                                const parsed = new Date(dateStr);
                                return !isNaN(parsed.getTime()) ? capitalize(formatDistance(parsed, new Date(), { addSuffix: true, locale: es })) : 'N/A';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'ko_reasons', permission: 'edit_ko_reasons' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar motivo de KO'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.states.path}${menuRoutes.states.koReasons}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'ko_reasons', permission: 'delete_ko_reasons' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar motivo de KO`}
                                                description={`¿Estás seguro de que deseas eliminar el motivo "${row.name}"?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiDeletePermission={{ group: 'ko_reasons', action: 'delete_ko_reasons' }}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage ?? 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Motivos de KO'}>
                <KoReasonsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default KoReasonsList;
